import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as L from "lucide-react";
import {
  MessageCircle, Brain, GitBranch, FileText, Send,
  Rocket, CheckCircle2, Users, Headphones, Zap,
  ZoomIn, ZoomOut, Maximize2,
} from "lucide-react";

/* ============================================================
   Visual Flow Builder — CMS-driven, auto-layout
   Nodes come from props (CMS content). The animation/render
   engine, timings, tones and visual style are owned by the
   component — admins only edit content.
   Branch field per node: "C" centered (full width), "L" left half,
   "R" right half. Two consecutive L/R nodes share a row.
   ============================================================ */

const TONES = {
  whatsapp: { bg: "bg-emerald-500", ring: "ring-emerald-300/60", pulse: "bg-emerald-500", hex: "#10b981", tint: "rgba(16,185,129,0.04)" },
  ai:       { bg: "bg-violet-500",  ring: "ring-violet-300/60",  pulse: "bg-violet-500",  hex: "#8b5cf6", tint: "rgba(139,92,246,0.04)" },
  cond:     { bg: "bg-orange-500",  ring: "ring-orange-300/60",  pulse: "bg-orange-500",  hex: "#f97316", tint: "rgba(249,115,22,0.04)" },
  sales:    { bg: "bg-pink-500",    ring: "ring-pink-300/60",    pulse: "bg-pink-500",    hex: "#ec4899", tint: "rgba(236,72,153,0.04)" },
  support:  { bg: "bg-blue-500",    ring: "ring-blue-300/60",    pulse: "bg-blue-500",    hex: "#3b82f6", tint: "rgba(59,130,246,0.04)" },
  crm:      { bg: "bg-sky-600",     ring: "ring-sky-300/60",     pulse: "bg-sky-600",     hex: "#0284c7", tint: "rgba(2,132,199,0.05)" },
  reply:    { bg: "bg-teal-500",    ring: "ring-teal-300/60",    pulse: "bg-teal-500",    hex: "#14b8a6", tint: "rgba(20,184,166,0.04)" },
  end:      { bg: "bg-gradient-to-br from-emerald-500 to-emerald-600", ring: "ring-emerald-300/70", pulse: "bg-emerald-500", hex: "#10b981", tint: "rgba(16,185,129,0.06)" },
};

const FALLBACK_ICONS = {
  whatsapp: MessageCircle, ai: Brain, cond: GitBranch, sales: Users,
  support: Headphones, crm: FileText, reply: Send, end: Rocket,
};

/* Layout constants — kept identical to the original visual */
const NODE_W = 320;
const NODE_H = 86;
const BRANCH_W = 270;
const CX = 140;
const LX = 18;
const RX = 312;
const ROW_PITCH = 114;
const ROW_Y_START = 16;

const STEP_MS = 1600;
const HOLD_MS = 4500;
const FADE_MS = 700;

const FALLBACK_NODES = [
  { id: "incoming", enabled: true, order: 1, icon: "MessageCircle", category: "WhatsApp",  title: "Incoming Message", status: "Received just now",   tone: "whatsapp", branch: "C", tip: "Trigger — customer sends a WhatsApp message." },
  { id: "ai",       enabled: true, order: 2, icon: "Brain",         category: "AI Engine", title: "Intent Detected",  status: "Confidence: 98%",     tone: "ai",       branch: "C", tip: "AI classifies the intent in 50+ languages." },
  { id: "route",    enabled: true, order: 3, icon: "GitBranch",     category: "Routing",   title: "Smart Routing",    status: "Sales · Support",     tone: "cond",     branch: "C", tip: "Routes the conversation to the right team based on intent." },
  { id: "sales",    enabled: true, order: 4, icon: "Users",         category: "Sales",     title: "Assigned",         status: "Agent · Rahul",       tone: "sales",    branch: "L", tip: "Assigns the conversation to the next available sales agent." },
  { id: "support",  enabled: true, order: 5, icon: "Headphones",    category: "Support",   title: "Assigned",         status: "Agent · Priya",       tone: "support",  branch: "R", tip: "Routes the query to the support team for fast resolution." },
  { id: "crm",      enabled: true, order: 6, icon: "FileText",      category: "CRM",       title: "Lead Created",     status: "ID #54821",           tone: "crm",      branch: "C", tip: "Logs the conversation and updates the lead record in your CRM." },
  { id: "reply",    enabled: true, order: 7, icon: "Send",          category: "WhatsApp",  title: "Send Reply",       status: "Template approved",   tone: "reply",    branch: "C", tip: "Sends a Meta-approved WhatsApp template back to the customer." },
  { id: "live",     enabled: true, order: 8, icon: "Rocket",        category: "Live",      title: "Automation Live",  status: "Uptime 100%",         tone: "end",      branch: "C", tip: "Workflow is live and serving real customers on WhatsApp." },
];

/* Auto-layout engine: groups nodes into rows; computes x/y/width per node;
   builds edges between consecutive rows. Works for any number of nodes. */
function buildLayout(rawNodes) {
  const cleaned = (Array.isArray(rawNodes) && rawNodes.length ? rawNodes : FALLBACK_NODES)
    .filter((n) => n && n.enabled !== false)
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  if (!cleaned.length) return { positioned: [], rows: [], edges: [], steps: 0 };

  // Group into rows: pair adjacent L/R into a single row; everything else solo full-width
  const rows = [];
  let i = 0;
  while (i < cleaned.length) {
    const n = cleaned[i];
    const branch = n.branch || "C";
    if ((branch === "L" || branch === "R") && cleaned[i + 1]) {
      const next = cleaned[i + 1];
      const nb = next.branch || "C";
      if ((nb === "L" || nb === "R") && nb !== branch) {
        rows.push(branch === "L" ? [n, next] : [next, n]);
        i += 2;
        continue;
      }
    }
    rows.push([n]);
    i += 1;
  }

  // Position each node
  const positioned = [];
  rows.forEach((row, rowIdx) => {
    const y = ROW_Y_START + rowIdx * ROW_PITCH;
    if (row.length === 1) {
      positioned.push({ ...row[0], _x: CX, _y: y, _w: NODE_W, _row: rowIdx });
    } else {
      // [left, right]
      positioned.push({ ...row[0], _x: LX, _y: y, _w: BRANCH_W, _row: rowIdx });
      positioned.push({ ...row[1], _x: RX, _y: y, _w: BRANCH_W, _row: rowIdx });
    }
  });

  // Edges: connect every node in row N to every node in row N+1
  const edges = [];
  for (let r = 0; r < rows.length - 1; r++) {
    const from = rows[r];
    const to = rows[r + 1];
    from.forEach((a) => to.forEach((b) => edges.push({ from: a.id, to: b.id, step: r })));
  }

  return {
    positioned,
    rows,
    edges,
    steps: rows.length,
  };
}

function StatusChip({ state }) {
  if (state === "active") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 px-1.5 py-0.5 font-bold" style={{ fontSize: 9 }} data-testid="status-active">
        <Zap style={{ width: 9, height: 9 }} className="text-amber-500" /> Processing
      </span>
    );
  }
  if (state === "done") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-1.5 py-0.5 font-bold" style={{ fontSize: 9 }} data-testid="status-done">
        <CheckCircle2 style={{ width: 9, height: 9 }} className="text-emerald-500" /> Completed
      </span>
    );
  }
  return null;
}

function WorkflowNode({ n, isActive, isDone, hovered, setHovered, isEndCelebrating, isEnd }) {
  const tone = TONES[n.tone] || TONES.whatsapp;
  const Icon = L[n.icon] || FALLBACK_ICONS[n.tone] || MessageCircle;
  const state = isActive ? "active" : isDone ? "done" : "pending";
  return (
    <foreignObject
      x={n._x}
      y={n._y - 4}
      width={n._w}
      height={NODE_H + 12}
      style={{ overflow: "visible" }}
      onMouseEnter={() => setHovered(n.id)}
      onMouseLeave={() => setHovered(null)}
    >
      <motion.div
        initial={{ opacity: 0, y: 8, scale: 0.96 }}
        animate={{
          opacity: 1, y: 0, scale: 1,
          boxShadow: isActive
            ? "0 0 0 0 rgba(16,185,129,0), 0 10px 24px rgba(16,185,129,0.16), 0 1px 3px rgba(2,6,23,0.06)"
            : "0 1px 2px rgba(2,6,23,0.05), 0 6px 16px rgba(2,6,23,0.05)",
        }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className={`relative rounded-2xl border mt-1 transition-all duration-500 ${
          isActive ? `ring-2 ${tone.ring} border-slate-200` : "border-slate-200"
        } ${isEnd && isEndCelebrating ? "ring-4 ring-emerald-300/70 shadow-[0_14px_36px_rgba(16,185,129,0.4)] border-emerald-200" : ""}`}
        style={{
          width: n._w,
          height: NODE_H,
          background: `linear-gradient(180deg, #ffffff 0%, ${tone.tint} 100%)`,
          borderLeft: `3px solid ${tone.hex}`,
        }}
        data-testid={`wf-node-${n.id}`}
      >
        {isActive && (
          <span className={`pointer-events-none absolute inset-0 rounded-2xl ${tone.pulse} opacity-[0.08]`} />
        )}
        <div className="flex items-center gap-3 px-3.5 h-full">
          <span className={`relative h-11 w-11 rounded-xl flex items-center justify-center text-white ${tone.bg} flex-shrink-0`}
            style={{ boxShadow: "inset 0 -1px 0 rgba(0,0,0,0.08), 0 2px 6px rgba(2,6,23,0.12)" }}>
            <Icon style={{ width: 19, height: 19 }} />
          </span>
          <div className="leading-tight min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="uppercase tracking-[0.08em] font-bold text-slate-500" style={{ fontSize: 9.5, lineHeight: 1.1 }}>
                {n.category}
              </span>
              <StatusChip state={state} />
            </div>
            <div className={`font-bold ${isEnd ? "text-emerald-700" : "text-slate-900"}`} style={{ fontSize: 14, lineHeight: 1.2, marginTop: 2 }}>
              {n.title}
            </div>
            <div className="text-slate-500" style={{ fontSize: 11, lineHeight: 1.15, marginTop: 1 }}>
              {n.status}
            </div>
          </div>
        </div>
        {hovered === n.id && n.tip && (
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full z-30 whitespace-nowrap rounded-md bg-slate-900 text-white px-2 py-1 shadow-lg pointer-events-none" style={{ fontSize: 11 }}>
            {n.tip}
            <span className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-slate-900 rotate-45" />
          </div>
        )}
      </motion.div>
    </foreignObject>
  );
}

function edgePath(a, b) {
  if (!a || !b) return "";
  const ax = a._x + a._w / 2;
  const ay = a._y + NODE_H;
  const bx = b._x + b._w / 2;
  const by = b._y;
  if (Math.abs(ax - bx) < 2) return `M ${ax} ${ay} L ${bx} ${by}`;
  const midY = (ay + by) / 2;
  return `M ${ax} ${ay} C ${ax} ${midY}, ${bx} ${midY}, ${bx} ${by}`;
}

export default function LeadQualificationFlow({ content }) {
  // Build layout from CMS content (or fallback) — memoised so animations don't reset on parent re-renders
  const { positioned, edges, steps, lastRowIdx } = useMemo(() => {
    const r = buildLayout(content?.nodes);
    const lastRow = r.rows.length - 1;
    return { ...r, lastRowIdx: lastRow };
  }, [content?.nodes]);

  const nodeById = useMemo(() => {
    const m = {};
    positioned.forEach((n) => { m[n.id] = n; });
    return m;
  }, [positioned]);

  const totalSteps = Math.max(steps, 1);
  const fullCycle = totalSteps * STEP_MS + HOLD_MS + FADE_MS;

  const [tick, setTick] = useState(0);
  const [step, setStep] = useState(-1);
  const [paused, setPaused] = useState(false);
  const [fading, setFading] = useState(false);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    if (paused) return;
    setStep(-1);
    setFading(false);
    const timers = [];
    for (let i = 0; i < totalSteps; i++) {
      timers.push(setTimeout(() => setStep(i), i * STEP_MS + 100));
    }
    timers.push(setTimeout(() => setFading(true), totalSteps * STEP_MS + HOLD_MS));
    timers.push(setTimeout(() => setTick((t) => t + 1), fullCycle));
    return () => timers.forEach(clearTimeout);
  }, [tick, paused, totalSteps, fullCycle]);

  const consoleTitle = content?.console_title || "Lead Qualification & Sales Automation";
  const completionTitle = content?.completion_title || "WhatsApp Automation Live";
  const completionSubtitle = content?.completion_subtitle || "Serving real customers";

  // viewBox height adapts to row count (keeps the dotted background centred)
  const viewBoxHeight = Math.max(800, ROW_Y_START + totalSteps * ROW_PITCH + 40);

  const isNodeVisible = (n) => step >= n._row;
  const isNodeActive  = (n) => step === n._row;
  const isNodeDone    = (n) => step > n._row;
  const isEndNode     = (n) => n._row === lastRowIdx;
  const isEndCelebrating = step >= lastRowIdx;
  const isComplete = step >= totalSteps - 1;

  if (!positioned.length) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      data-testid="chatbot-flow-builder"
      className="relative rounded-3xl bg-slate-50 border border-slate-200 p-4 sm:p-5 overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #cbd5e1 1px, transparent 1px)",
          backgroundSize: "18px 18px",
          opacity: 0.42,
          maskImage: "radial-gradient(ellipse 80% 70% at 50% 50%, #000 60%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 70% at 50% 50%, #000 60%, transparent 100%)",
        }}
      />

      <div className="relative">
        {/* Toolbar */}
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex gap-1.5">
              <span className="h-2 w-2 rounded-full bg-rose-400" />
              <span className="h-2 w-2 rounded-full bg-amber-400" />
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
            </div>
            <span className="uppercase tracking-[0.16em] font-bold text-slate-500 truncate" style={{ fontSize: 11 }}>
              {consoleTitle}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center bg-white rounded-full border border-slate-200 shadow-sm overflow-hidden" data-testid="wf-zoom-controls">
              <button type="button" aria-label="Zoom out" className="h-6 w-6 flex items-center justify-center text-slate-500 hover:bg-slate-50">
                <ZoomOut style={{ width: 12, height: 12 }} />
              </button>
              <span className="h-6 px-2 font-bold text-slate-600 flex items-center border-l border-r border-slate-100" style={{ fontSize: 10 }}>100%</span>
              <button type="button" aria-label="Zoom in" className="h-6 w-6 flex items-center justify-center text-slate-500 hover:bg-slate-50">
                <ZoomIn style={{ width: 12, height: 12 }} />
              </button>
              <button type="button" aria-label="Fit" className="h-6 w-6 flex items-center justify-center text-slate-500 hover:bg-slate-50 border-l border-slate-100">
                <Maximize2 style={{ width: 11, height: 11 }} />
              </button>
            </div>
            <div className="flex items-center gap-1.5 font-semibold text-slate-500" style={{ fontSize: 11 }}>
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Running
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tick}
            initial={{ opacity: 0 }}
            animate={{ opacity: fading ? 0 : 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.65 }}
            className="relative w-full"
          >
            <svg
              viewBox={`0 0 600 ${viewBoxHeight}`}
              preserveAspectRatio="xMidYMid meet"
              className="w-full h-auto"
              style={{ overflow: "visible" }}
            >
              <defs>
                <linearGradient id="edgeGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#94a3b8" stopOpacity="0.65" />
                  <stop offset="100%" stopColor="#475569" stopOpacity="0.65" />
                </linearGradient>
                <linearGradient id="edgeGradActive" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity="1" />
                  <stop offset="100%" stopColor="#10b981" stopOpacity="1" />
                </linearGradient>
                <radialGradient id="dotGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#34d399" stopOpacity="0.95" />
                  <stop offset="55%" stopColor="#10b981" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                </radialGradient>
                <radialGradient id="dotCore" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
                  <stop offset="55%" stopColor="#a7f3d0" stopOpacity="0.9" />
                  <stop offset="100%" stopColor="#10b981" stopOpacity="0.9" />
                </radialGradient>
                <filter id="trailBlur" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="2" />
                </filter>
              </defs>

              {edges.map((e, i) => {
                const a = nodeById[e.from];
                const b = nodeById[e.to];
                const d = edgePath(a, b);
                const isDrawn = step >= e.step;
                const isFlowing = step === e.step;
                return (
                  <g key={`e-${i}-${tick}`}>
                    <path d={d} stroke="#cbd5e1" strokeWidth="1.6" fill="none" strokeDasharray="4 6" opacity="0.6" />
                    <motion.path
                      d={d}
                      stroke={isFlowing ? "url(#edgeGradActive)" : "url(#edgeGrad)"}
                      strokeWidth={isFlowing ? 3.4 : 2.8}
                      fill="none"
                      strokeLinecap="round"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={isDrawn ? { pathLength: 1, opacity: isFlowing ? 1 : 0.75 } : { pathLength: 0, opacity: 0 }}
                      transition={{ duration: 0.45, ease: "easeInOut", delay: 0.08 }}
                    />
                    {isFlowing && (
                      <g>
                        <circle r="14" fill="url(#dotGlow)" opacity="0.8" filter="url(#trailBlur)">
                          <animateMotion dur="1.05s" repeatCount="1" fill="freeze" begin="0.55s" path={d} />
                        </circle>
                        <circle r="7" fill="url(#dotGlow)" opacity="0.5">
                          <animateMotion dur="1.05s" repeatCount="1" fill="freeze" begin="0.62s" path={d} />
                        </circle>
                        <circle r="5" fill="url(#dotGlow)" opacity="0.3">
                          <animateMotion dur="1.05s" repeatCount="1" fill="freeze" begin="0.7s" path={d} />
                        </circle>
                        <circle r="4.5" fill="url(#dotCore)">
                          <animateMotion dur="1.05s" repeatCount="1" fill="freeze" begin="0.55s" path={d} />
                        </circle>
                      </g>
                    )}
                  </g>
                );
              })}

              {positioned.map((n) => isNodeVisible(n) && (
                <WorkflowNode
                  key={`${n.id}-${tick}`}
                  n={n}
                  isActive={isNodeActive(n)}
                  isDone={isNodeDone(n)}
                  isEnd={isEndNode(n)}
                  isEndCelebrating={isEndNode(n) && isEndCelebrating}
                  hovered={hovered}
                  setHovered={setHovered}
                />
              ))}

              {isEndCelebrating && positioned.filter(isEndNode).map((n) => (
                <g key={`celeb-${n.id}`}>
                  {[...Array(8)].map((_, i) => {
                    const angle = (i / 8) * Math.PI * 2;
                    const cx = n._x + n._w / 2;
                    const cy = n._y + NODE_H / 2;
                    const x1 = cx + Math.cos(angle) * 50;
                    const y1 = cy + Math.sin(angle) * 50;
                    const x2 = cx + Math.cos(angle) * 82;
                    const y2 = cy + Math.sin(angle) * 82;
                    return (
                      <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#10b981" strokeWidth="1.6" strokeLinecap="round" opacity="0.4">
                        <animate attributeName="opacity" values="0;0.65;0" dur="2.0s" begin={`${i * 0.06}s`} repeatCount="indefinite" />
                      </line>
                    );
                  })}
                </g>
              ))}
            </svg>
          </motion.div>
        </AnimatePresence>

        <div className="mt-3 flex items-center justify-center min-h-[40px]">
          <AnimatePresence>
            {isComplete && !fading && (
              <motion.div
                initial={{ opacity: 0, y: 6, scale: 0.96 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: [1, 1.04, 1],
                  boxShadow: [
                    "0 4px 14px rgba(16,185,129,0.35)",
                    "0 10px 28px rgba(16,185,129,0.55)",
                    "0 4px 14px rgba(16,185,129,0.35)",
                  ],
                }}
                exit={{ opacity: 0, y: -4, scale: 0.96 }}
                transition={{
                  scale:     { duration: 2.4, repeat: Infinity, ease: "easeInOut" },
                  boxShadow: { duration: 2.4, repeat: Infinity, ease: "easeInOut" },
                  opacity:   { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
                  y:         { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
                }}
                className="inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-5 py-2"
                data-testid="wf-completed-chip"
              >
                <CheckCircle2 style={{ width: 16, height: 16 }} />
                <div className="leading-tight">
                  <div className="font-bold" style={{ fontSize: 13 }}>{completionTitle}</div>
                  <div className="opacity-90" style={{ fontSize: 10 }}>{completionSubtitle}</div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}
